<template>
    <v-card class="mb-5 pa-2">
        <card-header
            :title="
                $t('planPackages.form.texts.title', {
                    'title-prefix': titlePrefix,
                })
            "
        />

        <template v-if="!model">
            <v-container fluid class="fill-height">
                <v-row justify="center" align="center">
                    <v-col class="text-center" xs="12" md="12">
                        <v-progress-circular
                            :size="50"
                            color="primary"
                            indeterminate
                        />
                    </v-col>
                </v-row>
            </v-container>
        </template>

        <template v-else>
            <v-form ref="form" @submit.prevent="save">
                <v-card-text>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="model.name"
                                :label="$t('planPackages.form.labels.name')"
                                :error-messages="NameErrors"
                                @change="$v.model.name.$touch()"
                                required
                            />
                        </v-col>
                        <v-col cols="12" sm="3">
                            <v-currency-field
                                v-bind="$t('money-config')"
                                v-model="model.price"
                                :label="$t('planPackages.form.labels.price')"
                            />
                        </v-col>
                        <v-col cols="12" sm="3">
                            <v-currency-field
                                v-bind="$t('money-config')"
                                v-model="model.config_price"
                                :label="
                                    $t('planPackages.form.labels.config_price')
                                "
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="model.default_anual_discount"
                                :label="
                                    $t(
                                        'planPackages.form.labels.default_anual_discount',
                                    )
                                "
                                type="number"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="model.max_installments"
                                :label="
                                    $t(
                                        'planPackages.form.labels.max_installments',
                                    )
                                "
                                type="number"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="model.order"
                                :label="$t('planPackages.form.labels.order')"
                                type="number"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch
                                v-model="model.available_for_selection"
                                :label="
                                    $t(
                                        'planPackages.form.labels.available_for_selection',
                                    )
                                "
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-label>{{
                                $t('planPackages.form.labels.description')
                            }}</v-label>

                            <tiptap-vuetify
                                v-model="model.description"
                                :toolbar-attributes="{
                                    color: 'grey lighten-3',
                                }"
                                :extensions="tiptap.extensions"
                            ></tiptap-vuetify>
                        </v-col>
                    </v-row>
                </v-card-text>

                <form-actions :loadingSave="loading" />
            </v-form>
        </template>
    </v-card>
</template>

<script>
import CardHeader from '@/components/utils/CardHeader'
import FormActions from '@/components/utils/FormActions'
import BaseModel from '@/models/BaseModel'
import { required } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import {
    TiptapVuetify,
    Heading,
    Bold,
    Italic,
    Strike,
    Underline,
    Code,
    Paragraph,
    BulletList,
    OrderedList,
    ListItem,
    Link,
    Blockquote,
    HardBreak,
    HorizontalRule,
    History,
} from 'tiptap-vuetify'

export default {
    props: {
        planPackage: {
            type: Object,
            required: true,
            validator: model => model instanceof BaseModel,
        },
        isEdit: { type: Boolean, default: () => true },
        titlePrefix: String,
    },

    components: {
        'card-header': CardHeader,
        'form-actions': FormActions,
        TiptapVuetify,
    },

    mixins: [validationMixin],

    data: () => ({
        loading: false,
        tiptap: {
            extensions: [
                History,
                Blockquote,
                Link,
                Underline,
                Strike,
                Italic,
                ListItem,
                BulletList,
                OrderedList,
                [
                    Heading,
                    {
                        options: {
                            levels: [1, 2, 3],
                        },
                    },
                ],
                Bold,
                Code,
                HorizontalRule,
                Paragraph,
                HardBreak,
            ],
            editor: null,
        },
    }),

    validations: {
        model: {
            name: { required },
        },
    },

    computed: {
        model() {
            return this.planPackage
        },

        NameErrors() {
            const errors = []
            if (!this.$v.model.name.$dirty) return errors
            !this.$v.model.name.required &&
                errors.push(this.$t('planPackages.form.errors.name.required'))
            return errors
        },
    },

    methods: {
        async save() {
            this.loading = true

            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('form.validation-error'),
                )
                this.loading = false
                return
            }

            const clone = this.model.clone()
            let res = null
            try {
                res = await clone.save().catch(() => {})
            } finally {
                this.loading = false
            }
            if (!res) return
            this.$emit('saved')
            this.$bus.$emit(
                'notify',
                'success',
                this.$t('planPackages.form.feedback.success', {
                    name: clone.name,
                }),
            )
        },
    },
}
</script>
